import React from 'react';
import { Pie } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

// Register the required chart components
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * BugPieChart component for displaying bug data in a pie chart
 * @param {Object} props - Component props
 * @param {Array} props.data - The data to display in the chart
 * @param {string} props.title - The title of the chart
 * @param {string} props.groupBy - The field to group by (priority, state, issueSource)
 * @returns {JSX.Element} BugPieChart component
 */
const BugPieChart = ({ data, title, groupBy }) => {
  // Group the data by the specified field
  const groupedData = data.reduce((acc, bug) => {
    const key = bug[groupBy] || 'Unknown';
    if (!acc[key]) {
      acc[key] = 0;
    }
    acc[key]++;
    return acc;
  }, {});

  // Define colors based on the groupBy field
  const getColors = () => {
    if (groupBy === 'priority') {
      return {
        'P1': '#FF0000', // Red
        'P2': '#FFA500', // Orange
        'P3': '#00B050', // Green
        'P4': '#0070C0', // Blue
        'Unknown': '#CCCCCC' // Gray
      };
    } else if (groupBy === 'state') {
      return {
        'New': '#4CAF50', // Bright green
        'Closed': '#1B5E20', // Dark green
        'Resolved': '#A5D6A7', // Light green
        'Approved': '#9E9E9E', // Gray
        'Active': '#1A237E', // Dark blue
        'In Progress': '#FFC107', // Amber
        'Ready for Test': '#FF5722', // Deep orange
        'Ready for Review': '#2196F3', // Light blue
        'Blocked': '#D32F2F', // Red
        'Deferred': '#9C27B0', // Purple
        'Unknown': '#CCCCCC' // Light gray
      };
    } else if (groupBy === 'issueSource') {
      return {
        'Scrum QA': '#BDBDBD', // Light gray
        'Internal': '#3F51B5', // Indigo
        'UAT': '#9C27B0', // Purple
        'Regression QA': '#E91E63', // Pink
        'Automation': '#009688', // Teal
        'Customer': '#FF9800', // Orange
        'Partner': '#8BC34A', // Light green
        'Security': '#F44336', // Red
        'Performance': '#FFEB3B', // Yellow
        'Unknown': '#CCCCCC' // Light gray
      };
    }
    
    // Default colors
    return Object.keys(groupedData).reduce((acc, key, index) => {
      // Generate colors based on index
      const hue = (index * 137) % 360; // Use golden angle approximation for nice distribution
      acc[key] = `hsl(${hue}, 70%, 60%)`;
      return acc;
    }, {});
  };
  
  // Get colors for the current groupBy field
  const colorMap = getColors();
  
  // Prepare chart data
  const chartData = {
    labels: Object.keys(groupedData),
    datasets: [
      {
        data: Object.values(groupedData),
        backgroundColor: Object.keys(groupedData).map(key => colorMap[key] || '#CCCCCC'),
        borderColor: Object.keys(groupedData).map(key => colorMap[key] || '#CCCCCC'),
        borderWidth: 1,
      },
    ],
  };
  
  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '50%', // This makes it a donut chart instead of a pie chart
    plugins: {
      legend: {
        position: 'right',
        labels: {
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i];
                const backgroundColor = data.datasets[0].backgroundColor[i];
                
                return {
                  text: `${label} (${value})`,
                  fillStyle: backgroundColor,
                  strokeStyle: backgroundColor,
                  lineWidth: 1,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        formatter: (value, ctx) => {
          const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
          const percentage = Math.round((value / total) * 100);
          return value > 0 ? `${value}\n(${percentage}%)` : '';
        },
        color: '#fff',
        font: {
          weight: 'bold',
          size: 12
        }
      }
    }
  };
  
  return (
    <Box height={300} p={2}>
      <Typography variant="subtitle1" align="center" gutterBottom>
        {title}
      </Typography>
      <Box height={250}>
        <Pie data={chartData} options={options} />
      </Box>
    </Box>
  );
};

export default BugPieChart;
